import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ClipboardList, MapPin, Calendar, User, Shirt, PackageCheck, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import PrintEntregaButtons from '../conductor/PrintEntregaButtons';

/**
 * Modal exclusivo para alistamientos (registros aún no entregados).
 * Muestra los datos del alistamiento y las prendas, sin receptor ni firma,
 * y permite imprimir el ticket de alistamiento (packing slip).
 */
const DetalleAlistamientoModal = ({ isOpen, onClose, alistamiento }) => {
  if (!alistamiento) return null;

  const getTotalItems = () => {
    return alistamiento.items?.reduce((sum, i) => sum + (i.cantidad || 0), 0) || 0;
  };

  const getTotalKilos = () => {
    const totalGramos = alistamiento.items?.reduce((sum, i) => {
      const peso = i.peso || 0;
      const cantidad = i.cantidad || 0;
      return sum + (peso * cantidad);
    }, 0) || 0;
    return (totalGramos / 1000).toFixed(2);
  };

  const formatFecha = (fecha) => {
    if (!fecha) return 'N/A';
    return new Date(fecha).toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Datos para PrintEntregaButtons. El estado se mantiene para que el generador
  // de tickets produzca el ticket de alistamiento (no el comprobante de entrega).
  const ticketFormateado = {
    ...alistamiento,
    tipo: 'lenceria',
    consecutivo: alistamiento.solicitud_id || alistamiento.consecutivo,
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
          />

          {/* Modal */}
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
            >
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                    <ClipboardList className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                      Ticket de Alistamiento
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Lencería Vertical
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onClose}
                  className="rounded-full hover:bg-gray-200 dark:hover:bg-gray-700"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>

              {/* Content */}
              <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
                {/* Información del alistamiento */}
                <div className="space-y-4 mb-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-start space-x-3 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
                      <MapPin className="h-5 w-5 text-blue-600 mt-0.5" />
                      <div>
                        <p className="text-xs text-gray-600 dark:text-gray-400">Edificio</p>
                        <p className="font-semibold text-sm">{alistamiento.edificio?.nombre}</p>
                        <p className="text-xs text-gray-600">{alistamiento.edificio?.ciudad}</p>
                      </div>
                    </div>

                    {(alistamiento.hospedaje?.nombre || alistamiento.hospedaje?.nombrePropiedad) && (
                      <div className="flex items-start space-x-3 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
                        <Home className="h-5 w-5 text-green-600 mt-0.5" />
                        <div>
                          <p className="text-xs text-gray-600 dark:text-gray-400">Activo</p>
                          <p className="font-semibold text-sm">
                            {alistamiento.hospedaje?.nombre || alistamiento.hospedaje?.nombrePropiedad}
                          </p>
                        </div>
                      </div>
                    )}

                    <div className="flex items-start space-x-3 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
                      <Calendar className="h-5 w-5 text-green-600 mt-0.5" />
                      <div>
                        <p className="text-xs text-gray-600 dark:text-gray-400">Fecha de Alistamiento</p>
                        <p className="font-semibold text-sm">
                          {formatFecha(alistamiento.fecha_alistamiento || alistamiento.createdAt)}
                        </p>
                      </div>
                    </div>

                    {alistamiento.alistador && (
                      <div className="flex items-start space-x-3 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
                        <User className="h-5 w-5 text-purple-600 mt-0.5" />
                        <div>
                          <p className="text-xs text-gray-600 dark:text-gray-400">Alistado por</p>
                          <p className="font-semibold text-sm">
                            {alistamiento.alistador?.nombre || alistamiento.alistador || 'N/A'}
                          </p>
                        </div>
                      </div>
                    )}

                    {(alistamiento.solicitud_id || alistamiento.consecutivo) && (
                      <div className="flex items-start space-x-3 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
                        <ClipboardList className="h-5 w-5 text-orange-600 mt-0.5" />
                        <div>
                          <p className="text-xs text-gray-600 dark:text-gray-400">Consecutivo</p>
                          <p className="font-semibold text-sm font-mono">
                            {alistamiento.solicitud_id || alistamiento.consecutivo}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Aviso de estado pendiente */}
                  <div className="flex items-start space-x-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                    <PackageCheck className="h-5 w-5 text-blue-600 mt-0.5" />
                    <div>
                      <p className="text-xs text-gray-600 dark:text-gray-400">Estado</p>
                      <p className="font-semibold text-sm text-blue-700 dark:text-blue-400">
                        Alistado · Pendiente de entrega
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        Documento interno de alistamiento. No es comprobante de entrega.
                      </p>
                    </div>
                  </div>

                  {/* Observaciones */}
                  {alistamiento.observaciones && (
                    <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                      <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Observaciones</p>
                      <p className="text-sm text-gray-900 dark:text-gray-100">
                        {alistamiento.observaciones}
                      </p>
                    </div>
                  )}
                </div>

                {/* Items alistados */}
                <div className="mb-2">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-lg flex items-center gap-2">
                      <Shirt className="h-5 w-5 text-purple-600" />
                      Prendas Alistadas
                    </h3>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                        {getTotalItems()} items
                      </Badge>
                      <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                        {getTotalKilos()} kg
                      </Badge>
                    </div>
                  </div>

                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-gray-50 dark:bg-gray-900">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-400">
                            Item
                          </th>
                          <th className="px-4 py-2 text-center text-xs font-medium text-gray-600 dark:text-gray-400">
                            Peso (g)
                          </th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-600 dark:text-gray-400">
                            Cantidad
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {alistamiento.items?.map((item, idx) => (
                          <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-900">
                            <td className="px-4 py-3 text-sm">{item.nombre}</td>
                            <td className="px-4 py-3 text-sm text-center text-gray-600">
                              {item.peso || '-'}
                            </td>
                            <td className="px-4 py-3 text-sm text-right font-semibold">{item.cantidad}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between gap-3 p-6 border-t bg-gray-50 dark:bg-gray-900">
                <PrintEntregaButtons
                  entrega={ticketFormateado}
                  disabled={false}
                />
                <Button
                  variant="outline"
                  onClick={onClose}
                >
                  Cerrar
                </Button>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default DetalleAlistamientoModal;
